import { useCallback, useMemo } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import LoadingComponent from '@/components/general/Loading';
import ListHeader from '@/components/ListHeader';
import Menu from '@/components/Menu';
import { getEndpointWithParams } from '@/routes/config';
import Table, { Td, Th, Tr } from '@/trio-ui/Table';
import { CandidateTechStacksType } from '@/types';
import { InterviewReviewer } from '@/types/api/interview-reviewer';
import { Paths } from '@/types/routes';
import AddChallengeReviewerForm from './AddChallengeReviewerForm/AddChallengeReviewerForm.component';
import { EditReviewerData } from './ChallengeReviewers.container';
import * as Styles from './ChallengeReviewers.styles';

type ReviewerFallbackData = Partial<{
  firstName: string;
  lastName: string;
  developerType: string | null;
  pictureUrl: string | null;
  personId: string | null;
  candidateId: string | null;
  skills: CandidateTechStacksType[] | string[];
  user: {
    personId?: string | null;
  } | null;
}>;

const ChallengeReviewersComponent = ({
  isLoading,
  reviewers,
  isNewCandidateFormOpened,
  onShowCandidateFormClick,
  onHideCandidateFormClick,
  onAddNewReviewer,
  onDeleteReviewer,
  isEditFormOpened,
  reviewerToEdit,
  onHideEditFormClick,
  onShowEditFormClick,
}: {
  isLoading: boolean;
  reviewers: InterviewReviewer[];
  isNewCandidateFormOpened: boolean;
  onShowCandidateFormClick: (event: any) => void;
  onHideCandidateFormClick: () => void;
  onAddNewReviewer: (candidateId: string, skills: CandidateTechStacksType[]) => void;
  onDeleteReviewer: (interviewReviewerId: string) => void;
  isEditFormOpened: boolean;
  reviewerToEdit?: EditReviewerData;
  onHideEditFormClick: () => void;
  onShowEditFormClick: (reviewer: InterviewReviewer) => void;
}): JSX.Element => {
  const renderHeader = useMemo(() => {
    return (
      <Tr>
        <Th>Reviewer Name</Th>
        <Th>Developer Type</Th>
        <Th>Skills</Th>
        <Th>Actions</Th>
      </Tr>
    );
  }, []);

  const renderRow = useCallback(
    (interviewReviewer: InterviewReviewer) => {
      const { id: interviewReviewerId } = interviewReviewer;
      const reviewer = interviewReviewer.person as InterviewReviewer['person'] | null | undefined;
      const reviewerFallback = interviewReviewer as ReviewerFallbackData;
      const explicitPersonId = reviewerFallback.personId;
      const explicitCandidateId = reviewerFallback.candidateId;
      const explicitUserPersonId = reviewerFallback.user?.personId;
      const hasExplicitMissingPersonMarker =
        explicitPersonId === null || explicitCandidateId === null || explicitUserPersonId === null;
      const reviewerPersonId =
        reviewer?.id || explicitPersonId || explicitCandidateId || explicitUserPersonId || '';
      const hasMissingPerson = hasExplicitMissingPersonMarker || !reviewerPersonId;
      const firstName = reviewerFallback.firstName || reviewer?.firstName || '';
      const lastName = reviewerFallback.lastName || reviewer?.lastName || '';
      const reviewerDisplayName = `${firstName} ${lastName}`.trim() || 'Unknown reviewer';
      const developerType = reviewerFallback.developerType || reviewer?.developerType || '--';
      const pictureUrl = reviewerFallback.pictureUrl || reviewer?.pictureUrl || '';
      const reviewerLink = reviewerPersonId
        ? getEndpointWithParams(Paths.CANDIDATE_DETAILS, {
            id: reviewerPersonId,
          })
        : '';
      let reviewerSkillsSource: unknown[] = [];

      if (Array.isArray(reviewerFallback.skills)) {
        reviewerSkillsSource = reviewerFallback.skills;
      } else if (Array.isArray(reviewer?.skills)) {
        reviewerSkillsSource = reviewer?.skills ?? [];
      }
      const reviewerSkills = reviewerSkillsSource.filter(
        (skill): skill is CandidateTechStacksType => {
          return typeof skill === 'object' && skill !== null && 'skill' in skill;
        }
      );
      const willingToReviewSkills = reviewerSkills.filter(
        (skill) => skill?.willingToReview && skill?.skill?.name
      );
      const rowActionId = reviewerPersonId || interviewReviewerId;

      return (
        <Tr key={interviewReviewerId}>
          <Td>
            <Styles.Centered>
              <Styles.CustomUserInitials
                size={24}
                pictureUrl={pictureUrl}
                firstName={firstName}
                lastName={lastName}
              />
              {reviewerLink ? (
                <Styles.TableLink to={reviewerLink} className="capitalize">
                  {reviewerDisplayName}
                </Styles.TableLink>
              ) : (
                <p className="tw-font-semibold tw-capitalize">{reviewerDisplayName}</p>
              )}
            </Styles.Centered>
          </Td>

          <Td>{developerType}</Td>

          <Td style={{ display: 'flex', flexWrap: 'wrap', minWidth: '300px', minHeight: '44px' }}>
            {willingToReviewSkills.map((skill: CandidateTechStacksType, index: number) => (
              <Styles.CustomTechStackBadge
                key={`${skill.skill.name}-${rowActionId}-${index.toString()}`}
                title={skill.skill.name}
                willingToReview={skill.willingToReview}
              >
                {skill.skill.name}
              </Styles.CustomTechStackBadge>
            ))}
          </Td>

          <Td textAlign="center">
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-1">
              <Menu
                icon={MoreHorizIcon}
                menuId={`reviewer-actions-${rowActionId}`}
                menuItems={[
                  {
                    label: 'Edit',
                    menuItemIcon: EditIcon,
                    callback: () => {
                      onShowEditFormClick(interviewReviewer);
                    },
                  },
                  {
                    label: 'Delete',
                    menuItemIcon: DeleteIcon,
                    menuItemOptions: { warning: true },
                    callback: () => {
                      onDeleteReviewer(interviewReviewerId);
                    },
                  },
                ]}
              />
              {hasMissingPerson && (
                <span
                  className="tw-text-xs tw-font-semibold tw-leading-none"
                  data-testid={`missing-person-indicator-reviewer-${interviewReviewerId}`}
                >
                  X
                </span>
              )}
            </div>
          </Td>
        </Tr>
      );
    },
    [onDeleteReviewer, onShowEditFormClick]
  );

  return (
    <Styles.Content data-testid="challenge-reviewers-list">
      <ListHeader
        title="Reviewers"
        showActionsArea
        showLinks={false}
        showSearchBar={false}
        onShowCandidateFormClick={onShowCandidateFormClick}
      />

      <AddChallengeReviewerForm
        isOpened={isNewCandidateFormOpened}
        onClose={onHideCandidateFormClick}
        onAddReviewer={onAddNewReviewer}
      />

      <AddChallengeReviewerForm
        isOpened={isEditFormOpened}
        onClose={onHideEditFormClick}
        onAddReviewer={onAddNewReviewer}
        reviewerToEdit={reviewerToEdit}
      />

      {isLoading && <LoadingComponent fullPage />}

      {!isLoading && !reviewers.length && (
        <Styles.NotFound>
          <Styles.NotFoundSubtitle>No reviewers found</Styles.NotFoundSubtitle>
          <p>Add reviewers to start reviewing challenge submissions.</p>
        </Styles.NotFound>
      )}

      {!isLoading && !!reviewers.length && (
        <Table renderHeader={renderHeader} renderRow={renderRow} data={reviewers} />
      )}
    </Styles.Content>
  );
};

export default ChallengeReviewersComponent;
